import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * How the AI-action surfaces present themselves while an action runs.
 *
 * **Per viewer, per device, and there is no endpoint for it** — the platform stores nothing of
 * the kind, and inventing a server round-trip for a display preference would be a worse lie
 * than saying so here. Two people in the same workspace can disagree about how loud the
 * reasoning trace should be, and both are right.
 */
export const THOUGHT_DISPLAY_MODES = ["verbose", "summary", "minimal", "none"] as const;
export type ThoughtDisplayMode = (typeof THOUGHT_DISPLAY_MODES)[number];

export const DRAWER_POSITIONS = ["auto", "end", "start", "bottom"] as const;
export type DrawerPosition = (typeof DRAWER_POSITIONS)[number];

interface AiActionPreferences {
  /** How much of the model's reasoning is shown while it works. */
  thoughtDisplay: ThoughtDisplayMode;
  /**
   * Where the results drawer opens. Logical, not physical: `start`/`end` follow the reading
   * direction, so the same preference means the mirrored thing in a Hebrew UI.
   */
  drawerPosition: DrawerPosition;
  setThoughtDisplay(mode: ThoughtDisplayMode): void;
  setDrawerPosition(position: DrawerPosition): void;
}

export const useAiActionPreferences = create<AiActionPreferences>()(
  persist(
    (set) => ({
      thoughtDisplay: "summary",
      drawerPosition: "auto",
      setThoughtDisplay: (thoughtDisplay) => set({ thoughtDisplay }),
      setDrawerPosition: (drawerPosition) => set({ drawerPosition }),
    }),
    {
      name: "slate.aiActions",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
